import { useEffect, useRef } from 'react';
import {
  CO_USER_MAX_ALLOWED_VALUE,
  CO_USER_MIN_ALLOWED_VALUE,
  EU,
  GAMMA,
  INTEG,
  MASTER,
  PROD,
  SHIFT_PLAN_SUGGESTED_INPUT
} from './constants';
import moment from 'moment';
import { getCountryCode } from '../handler/parentDataHandler';

export const chainWalk = (func, defaultValue) => {
  try {
    const value = func();
    return value === null || value === undefined ? defaultValue : value;
  } catch (e) {
    return defaultValue;
  }
};

export let convertEpochToDatetimeLocal = (epoch, timezone) => {
  return moment.unix(epoch).tz(timezone).format('YYYY-MM-DD[T]HH:mm:SS.[000Z]');
};

export let parseEpochToDateStringWithOptions = (val, formatOptions) => {
  let dateObject = new Date(parseFloat(val) * 1000);
  return dateObject.toLocaleString('en-US', formatOptions);
};

export let parseEpochToDateStringWithTimeZone = (val, timezone) => {
  let dateObject = new Date(parseFloat(val) * 1000);
  const options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    hourCycle: 'h23',
    timeZone: timezone.toString()
  };
  return dateObject.toLocaleString('en-US', options);
};

export const parseEpochToTimeRefresherDateString = (val) => {
  let dateObject = new Date(parseFloat(val) * 1000);
  const DATE_TIME_FORMAT = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    hourCycle: 'h23'
  };
  return new Intl.DateTimeFormat('en-US', DATE_TIME_FORMAT).format(dateObject);
};

export const isValid = (val) => {
  return !(val === null || val === undefined);
};

export const isStringValid = (val) => {
  return isValid(val) && typeof val === 'string';
};

export const isEmpty = (val) => {
  return !isStringValid(val) || val.trim().length === 0;
};

export const planTypeFormat = (planType) => {
  if (planType === 'NEXT_DAY') return 'Next Day Shift';
  else if (planType === 'PRE_SHIFT') return 'Pre Shift';
  else return 'Start Shift';
};

export const convertToFixedDP = (val, dp) => {
  return parseFloat(parseFloat(val).toFixed(isValid(dp) ? dp : 1));
};

export const convertTimeStampToEpoch = (val) => {
  return new Date(val).getTime() / 1000;
};

export const changeDateFormat = (date) => {
  let [y, m, d] = date.split('-');
  return `${m}/${d}/${y}`;
};

export const fetchLocale = async () => {
  const countryCode = await getCountryCode();
  return JSON.parse(countryCode['locale']);
};

export const isDateOld = (date) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [year, month, day] = date.split('-');
  const givenDate = new Date(year, month - 1, day);
  return givenDate < today;
};

export const extractMainWorkGroup = (workGroups) => {
  if (!workGroups) {
    return '';
  }
  let mainWorkGroup = workGroups.filter((workGroup) => workGroup.endsWith('(Main Activity)'))[0];
  if (!mainWorkGroup) {
    return '';
  }
  return mainWorkGroup.replace('(Main Activity)', '').trim();
};

export const getWindowDurationInMin = (start, end) => {
  const d1 = new Date(start);
  const d2 = new Date(end);
  let diff = (d2 - d1) / 60000;
  return Math.abs(Math.round(diff));
};

export const isDateSubwindow = (parentStart, parentEnd, childStart, childEnd) => {
  return parentStart <= childStart && childEnd <= parentEnd;
};

export const isDateStringSubwindow = (parentStart, parentEnd, childStart, childEnd) => {
  return isDateSubwindow(new Date(parentStart), new Date(parentEnd), new Date(childStart), new Date(childEnd));
};

export const parseTimeStampToDateString = (val) => {
  let time = val.split('T')[1];
  let [hr, min] = time.split(':');
  return `${hr}:${min}`;
};

export const formatTimeWindow = (window) => {
  if (!window?.start || !window?.end) return '—';
  return `${parseTimeStampToDateString(window.start)} – ${parseTimeStampToDateString(window.end)}`;
};

export const parseTimeStampToDateStringWith12Hr = (val) => {
  let time = val.split('T')[1];
  let [hr, min] = time.split(':');
  let newHr = hr > 12 ? hr - 12 : hr;
  let m = hr >= 12 ? 'p' : 'a';

  return `${newHr}:${min}${m}`;
};

const zeroPad = (num) => String(num).padStart(2, '0');

export let parseEpochToDateString = (val) => {
  let dateObject = new Date(parseFloat(val) * 1000);
  let day = zeroPad(dateObject.getDate());
  let month = zeroPad(dateObject.getMonth() + 1);
  let year = dateObject.getFullYear();
  let time = dateObject.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  return `${day}/${month}/${year} ${time}`;
};

export let parseEpochToHourMinute = (epochMillis, timeZone) => {
  const date = new Date(epochMillis);
  const formatter = new Intl.DateTimeFormat([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timeZone
  });
  return formatter.format(date);
};

export let generateDateIncrements = (startDateMillis, endDateMillis, incrementMillis) => {
  const dateIncrements = [startDateMillis];

  for (let i = 0; i <= (endDateMillis - startDateMillis) / incrementMillis; i++) {
    dateIncrements.push(startDateMillis + i * incrementMillis);
  }

  return dateIncrements;
};

/** Fetches list of dates from `curr - numDaysAvailablePast` to `curr + numDaysAvailableFuture` days
 * Both params default to 7 days, totalling a 15 day window. */
export const getAvailableDates = (numDaysAvailablePast = 7, numDaysAvailableFuture = 7) => {
  let res = [];
  let date = new Date();
  date.setDate(date.getDate() - numDaysAvailablePast - 1);
  for (let i = 0; i <= numDaysAvailablePast + numDaysAvailableFuture; i++) {
    date.setDate(date.getDate() + 1);
    res.push(formatDateForAPI(date));
  }
  return res;
};

export const getCurrentEpoch = () => {
  return new Date().getTime() / 1000;
};

export const formatDateForAPI = (date) => {
  let mm = date.getMonth() + 1;
  let dd = date.getDate();
  return [date.getFullYear(), '-', (mm > 9 ? '' : '0') + mm, '-', (dd > 9 ? '' : '0') + dd].join('');
};

export const formatDateForGraph = (dateInSeconds, timezone, options = {}) => {
  const utcDate = new Date(dateInSeconds * 1000);
  const defaultOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options
  };
  const dateFormatter = new Intl.DateTimeFormat('en-US', defaultOptions);
  const dateParts = dateFormatter.formatToParts(utcDate).map((p) => p.value);
  return [dateParts[4], '-', dateParts[0], '-', dateParts[2], ' ', dateParts[6], ':', dateParts[8]].join('');
};

export const getTodaysDate = () => {
  let date = new Date();
  return formatDateForAPI(date);
};

export const isNumberInvalid = (val) => {
  return val === undefined || val === null || val === '' || !isFinite(val);
};

export const isNumberNotInRange = (val, minAllowed, maxAllowed, isUserAuthorisedForShiftPlanOverrides = false) => {
  return isUserAuthorisedForShiftPlanOverrides
    ? val < CO_USER_MIN_ALLOWED_VALUE || val > CO_USER_MAX_ALLOWED_VALUE
    : val < minAllowed || val > maxAllowed;
};

export const moveHardCodedElementsToBeginningOfArray = (elementArray, array) => {
  return elementArray
    .filter((element) => array.includes(element))
    .concat(array.filter((element) => !elementArray.includes(element)));
};

export const isJSONEmpty = (jsonObject) => {
  return (
    jsonObject && // 👈 null and undefined check
    Object.keys(jsonObject).length === 0 &&
    Object.getPrototypeOf(jsonObject) === Object.prototype
  );
};

export const addFifteenMinutesToEpoch = (epoch) => {
  return epoch + 15 * 60;
};

export const removeSecondsFromTimeStamp = (timeStamp) => {
  let [hr, min] = timeStamp.split(':');
  return `${hr}:${min}`;
};

export const alphabeticalSort =
  (key, direction = 'ascending') =>
  (a, b) => {
    const directionMultiplier = 'ascending' === direction ? 1 : -1;
    if ((a[key] === null || a[key] === undefined) && (b[key] === null || b[key] === undefined)) {
      return 0;
    }
    if (a[key] === null || a[key] === undefined) {
      return directionMultiplier;
    }
    if (b[key] === null || b[key] === undefined) {
      return -1 * directionMultiplier;
    }
    return directionMultiplier * (a[key].toLowerCase() > b[key].toLowerCase() ? 1 : -1);
  };

export const getRegion = () => {
  const url = window.location.href;
  if (url.includes('eu.')) return 'eu';
  if (url.includes('jp.')) return 'jp';
  if (url.includes('in.')) return 'in';
  return 'na';
};

export const compareDates = (date1, date2) => {
  return date1 < date2;
};

export function getDomain() {
  const url = window.location.href;
  return url.includes(INTEG) ? INTEG : url.includes(GAMMA) ? GAMMA : url.includes(PROD) ? PROD : null;
}

export const isRegionEU = () => {
  return getRegion() === EU;
};

export const isSSDCycle = (cycleName) => {
  if (cycleName === null || cycleName === undefined) {
    return false;
  }
  const regex = /^CYCLE_SD_.$/i;
  return regex.test(cycleName);
};

export const sortList = (list) => {
  return list !== null && list !== undefined ? list.sort() : list;
};

// Generate interval times from minGraphSeconds to maxGraphSeconds
export const generateIntervals = (minSeconds, maxSeconds, intervalMinutes) => {
  if (minSeconds === Number.MAX_SAFE_INTEGER || maxSeconds === 0) {
    return [];
  }

  const intervals = [];
  const intervalSeconds = intervalMinutes * 60;

  const startTime = Math.floor(minSeconds / intervalSeconds) * intervalSeconds;
  const endTime = Math.ceil(maxSeconds / intervalSeconds) * intervalSeconds;

  for (let time = startTime; time <= endTime; time += intervalSeconds) {
    intervals.push(new Date(time * 1000).toISOString().substring(0, 16).replace('T', ' '));
  }

  return intervals;
};

export function combineDynamicShiftPlanBetaTableData(
  tableData,
  keys,
  destinationKey,
  processTypes = ['planned', 'actual']
) {
  if (!(destinationKey in tableData)) {
    tableData[destinationKey] = {
      planned: [],
      actual: []
    };
  }
  const filteredKeys = keys.filter((key) => key in tableData);
  filteredKeys.forEach((filteredKey) => {
    const processData = tableData[filteredKey];
    processTypes.forEach((processType) => {
      processData[processType].forEach((processDatum) => {
        const existingDatum = tableData[destinationKey][processType].find(
          (averagedDatum) => averagedDatum.startInSeconds === processDatum.startInSeconds
        );
        if (existingDatum) {
          existingDatum.value += processDatum.value;
        } else {
          tableData[destinationKey][processType].push({
            startInSeconds: processDatum.startInSeconds,
            value: processDatum.value
          });
        }
      });
    });
  });
}

// Transform HeadcountPlan items into indirectHeadcountAllocations
export function headcountPlanToIndirectHeadcountAllocations(headcountPlan) {
  const indirectHeadcoutAllocations = [];

  if (!headcountPlan) {
    return indirectHeadcoutAllocations;
  }

  headcountPlan.forEach((headcountCycle) => {
    const cycleName = headcountCycle['cycle'];
    const hcPlan = headcountCycle['headCountPlannerOutput'];

    // Get time windows
    const timeKeys = Object.keys(hcPlan).sort();
    const hcPlanTimeWindows = [];
    let laborProcesses = [];
    for (let i = 0; i < timeKeys.length - 1; i++) {
      hcPlanTimeWindows.push({
        startTime: timeKeys[i],
        endTime: timeKeys[i + 1]
      });
    }
    if (timeKeys.length > 0) {
      // Append last time window
      const lastWindowEnd = new Date(timeKeys[timeKeys.length - 1]);
      lastWindowEnd.setMinutes(lastWindowEnd.getMinutes() + 15);
      hcPlanTimeWindows.push({
        startTime: timeKeys[timeKeys.length - 1],
        endTime: formatDateForGraph(lastWindowEnd.getTime() / 1000).replace(' ', 'T')
      });

      // Get list of labor processes
      laborProcesses = chainWalk(() => Object.keys(hcPlan[timeKeys[0]]).sort(), []);
    }

    hcPlanTimeWindows.forEach(({ startTime, endTime }) => {
      const hcPlanForTimeWindow = hcPlan[startTime];
      Object.entries(hcPlanForTimeWindow).forEach(([laborProcess, valueObject]) => {
        const assignedHeadcount =
          valueObject['assignedHeadCounts'] === null || valueObject['assignedHeadCounts'].length === 0
            ? null
            : valueObject['assignedHeadCounts'][0]['assignedHeadCount'];
        const indirectHcAllocation = {
          laborProcess: laborProcess,
          startTime: startTime,
          endTime: endTime,
          assignedHeadCount: assignedHeadcount,
          cycleName: cycleName,
          isOverriden: false
        };
        indirectHeadcoutAllocations.push(indirectHcAllocation);
      });
    });
  });
  return indirectHeadcoutAllocations;
}

// Looks for an object in a sorted list based on the specified field
// If found returns the index and object, if not found returns the index
// and the previous object in the list.
export function findInSortedList(list, value, field = 'id') {
  if (!Array.isArray(list) || list.length === 0) {
    return { found: false, index: -1 };
  }

  let left = 0;
  let right = list.length - 1;

  try {
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const midValue = list[mid][field];

      if (midValue === value) {
        return { found: true, index: mid, item: list[mid] };
      }

      if (midValue < value) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return {
      found: false,
      index: right,
      previousItem: right >= 0 ? list[right] : null
    };
  } catch (error) {
    console.error('Error searching list:', error);
    return { found: false, index: -1 };
  }
}

// https://code.amazon.com/packages/ACE-EngagementPortal/blobs/af7aa468b02d39b8a9e3609492e5a1137addf290/--/apps/frontend-layer/frontend/src/ts/core/hooks/useInterval.ts
/**
 * Custom Interval Hook from https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 * @param {Function} callback - Function to be called on interval
 * @param {number} delay - Interval delay in milliseconds
 * @param {Function} cleanup - Function to be called upon cleanup
 * @param {number} timeout - Timeout expiration time in milliseconds
 */
export function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function trimAndCapitalizeFirstLetter(str = '') {
  const trimmed = str.trim();
  if (!trimmed) return '';
  const [first, ...rest] = [...trimmed];
  return first.toUpperCase() + rest.join('').toLowerCase();
}

/**
 * Extracts available cycles from opsClock data
 * @param {Object} opsClockData - OpsClock data containing clockEvents
 * @returns {Array<string>} Array of unique cycle names
 */
export const getAvailableCycles = (opsClockData) => {
  const cycleNames = new Set();
  (opsClockData?.clockEvents || []).forEach((event) => {
    if (event.cycleName) {
      cycleNames.add(event.cycleName);
    }
  });

  return Array.from(cycleNames).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
};

/**
 * Gets display name for a cycle
 * @param {string} cycleName - Cycle name from opsClock
 * @returns {string} User-friendly display name
 */
const CYCLE_DISPLAY_NAMES = {
  CYCLE_0: 'Cycle 0',
  CYCLE_1: 'Cycle 1',
  CYCLE_2: 'Cycle 2',
  CYCLE_3: 'Cycle 3',
  CYCLE_4: 'Cycle 4',
  SAME_DAY: 'Same Day',
  SAME_DAY_AM: 'Same Day AM',
  SAME_DAY_DINNER: 'Same Day Dinner',
  SAME_DAY_SUNRISE: 'Same Day Sunrise'
};

export const getCycleDisplayName = (cycleName) => CYCLE_DISPLAY_NAMES[cycleName] || cycleName;

export const normalizeTime = (t) => {
  if (!t) return '';
  const trimmed = t.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
